import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { euro } from "@/lib/accounting";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => `${today().slice(0, 7)}-01`;

export const FromProductionDialog = ({ open, onClose, onCreated }) => {
  const { visibleCompanies, parties, projects, companyId } = useData();
  const [f, setF] = useState({ company_id: companyId || "", customer_id: "", project_id: "", date_from: firstOfMonth(), date_to: today(), include_materials: true, date: today() });
  const [prev, setPrev] = useState(null);
  const set = (p) => { setF((s) => ({ ...s, ...p })); setPrev(null); };
  const body = () => ({ ...f, project_id: f.project_id || null });
  const preview = async () => {
    try { const { data } = await api.post("/accounting/from-production/preview", body()); setPrev(data); } catch (e) { toast.error(errMsg(e)); }
  };
  const create = async () => {
    try { const { data } = await api.post("/accounting/from-production", body()); toast.success("Bozza di fattura creata"); onCreated(data); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  const sel = (key, options, testId) => (
    <select className="mt-1.5 h-10 w-full border bg-white px-2 text-sm" value={f[key]} onChange={(e) => set({ [key]: e.target.value })} data-testid={testId}>
      <option value="">{key === "project_id" ? "Tutte le commesse" : "Seleziona"}</option>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="from-production-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Fattura dalla produzione contabilizzabile</DialogTitle></DialogHeader>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><Label className="micro-label">Società (titolare della commessa)</Label>{sel("company_id", visibleCompanies.map((c) => [c.id, c.legal_name]), "fp-company")}</div>
          <div><Label className="micro-label">Committente</Label>{sel("customer_id", parties.filter((p) => p.roles.includes("customer")).map((p) => [p.id, p.name]), "fp-customer")}</div>
          <div><Label className="micro-label">Commessa</Label>{sel("project_id", projects.filter((p) => !f.customer_id || p.customer_id === f.customer_id).map((p) => [p.id, `${p.code} · ${p.name}`]), "fp-project")}</div>
          <div><Label className="micro-label">Data fattura</Label><Input type="date" className="mt-1.5" value={f.date} onChange={(e) => set({ date: e.target.value })} data-testid="fp-date" /></div>
          <div><Label className="micro-label">Produzione dal</Label><Input type="date" className="mt-1.5" value={f.date_from} onChange={(e) => set({ date_from: e.target.value })} data-testid="fp-from" /></div>
          <div><Label className="micro-label">al</Label><Input type="date" className="mt-1.5" value={f.date_to} onChange={(e) => set({ date_to: e.target.value })} data-testid="fp-to" /></div>
          <div className="flex items-center gap-3"><Switch checked={f.include_materials} onCheckedChange={(v) => set({ include_materials: v })} data-testid="fp-materials" /><Label>Addebita i materiali al prezzo di listino</Label></div>
        </div>
        {prev && (
          <div className="mt-4 border bg-white" data-testid="fp-preview">
            <div className="border-b bg-muted/40 px-3 py-2 text-sm">{prev.orders} interventi · {prev.lines.length} righe {prev.reverse_charge && "· reverse charge"}</div>
            {prev.lines.map((l, i) => <div key={i} className="flex justify-between border-b px-3 py-1.5 text-sm"><span><span className="font-mono text-xs">{l.code}</span> {l.description} × {l.qty}</span><span className="font-mono">{euro(l.amount)}</span></div>)}
            <div className="flex justify-end gap-6 px-3 py-2 text-sm"><span>Imponibile <strong>{euro(prev.taxable)}</strong></span><span>IVA <strong>{euro(prev.vat)}</strong></span><span>Totale <strong data-testid="fp-total">{euro(prev.total)}</strong></span></div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={preview} disabled={!f.company_id || !f.customer_id} data-testid="fp-preview-button">Anteprima</Button>
          <Button onClick={create} disabled={!prev || !prev.lines.length} data-testid="fp-create-button">Crea bozza</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
